import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Dropdown, DropdownItem } from "flowbite-react";
import { Button } from "@/components/ui/Button/Button";
import { Typography } from "@/components/ui/Typography/Typography";
import { Badge } from "@/components/ui/Badge/badge";
import "../../RoleManagement/components/role-management-theme.css";
import { TAssignedPermission } from "@/api/role-management/role-management.types";
import { usePermissionModules } from "@/api/role-management/role-management.hooks";
import {
  useEmployeeAssignedPermissions,
  useEmployeeAvailablePermissions,
  useBulkEmployeePermissionAssignment,
  useEmployeePermissionToggle,
} from "@/api/employee-management/employee-management.hooks";
import type {
  TBulkEmployeePermissionAssignmentRequest,
  TEmployeePermissionToggleVariables,
} from "@/api/employee-management/employee-management.types";
import type { TApiServiceError } from "@/api/apiService";
import {
  Search,
  Shield,
  CheckCircle,
  Circle,
  Plus,
  Minus,
  Users,
} from "lucide-react";
import { Input, Select, TSelectOption } from "@/components/forms";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@/hooks";
import KpiLoaders from "@/pages/layout/loaders/KpiLoaders";
import { PAGE_INDEX, PAGE_LIMIT_TEN } from "@/core/const";
import { PaginationState } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import CommonPaginationCard from "@/components/ui/Table/CommonPaginationCard";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useBreadcrumb } from "@/hooks/useBreadcumb";
import CommonCardUI from "@/components/ui/Card/CommonCardUI";
import { cn } from "@/utils";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermissions } from "@/hooks/usePermissions";
import ErrorFallback from "@/components/error-fallback/ErrorFallback";
import { useToast } from "@/components/ui/Toast/useToast";

// Use the enhanced permission types from the API
type TPermission = TAssignedPermission;
type TPermissionFiltersState = {
  searchQuery: string;
  selectedModuleKey: string;
  selectedModelKey: string;
};
type TSelectedPermissionIdsState = {
  assign: string[];
  remove: string[];
};
type TPermissionAssignmentViewState = {
  activeBulkAction: "assign" | "remove" | null;
  isNavigatingBack: boolean;
};
type TPermissionRowProps = {
  permission: TPermission;
  isChecked: boolean;
  isToggling: boolean;
  pendingAction?: "assign" | "remove";
  isBulkActionRunning: boolean;
  canAssignEmployeePermissions: boolean;
  onSelect: (permission: TPermission) => void;
  onToggle: (permission: TPermission) => void;
};

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100] as const;
const DEFAULT_PERMISSION_FILTERS: TPermissionFiltersState = {
  searchQuery: "",
  selectedModuleKey: "all",
  selectedModelKey: "all",
};

const PermissionRowComponent: React.FC<TPermissionRowProps> = ({
  permission,
  isChecked,
  isToggling,
  pendingAction,
  isBulkActionRunning,
  canAssignEmployeePermissions,
  onSelect,
  onToggle,
}) => {
  const { t } = useTranslation(["roleManagementPage", "common"]);
  return (
    <div className="group relative">
      <div
        className={`flex items-center gap-3 p-3 border-b ${isToggling || isBulkActionRunning ? "" : "hover:bg-muted/25  transition-colors"}`}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => onSelect(permission)}
          disabled={
            isToggling || isBulkActionRunning || !canAssignEmployeePermissions
          }
          className="rounded border-border"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Typography variant="p" className="font-medium text-foreground">
              {permission.name ?? t("PERMISSION_ASSIGNMENT.UNNAMED_PERMISSION")}
            </Typography>
          </div>
        </div>

        <PermissionGuard
          permissions={[
            "can-assign-permissions",
            "can-assign-permissions-to-employee",
          ]}
        >
          <Button
            variant={permission.is_assigned ? "destructive" : "default"}
            size="xs"
            onClick={() => void onToggle(permission)}
            disabled={isToggling || isBulkActionRunning}
            className={`flex items-center gap-1 transition-opacity duration-200
              ${
                isToggling || isBulkActionRunning
                  ? "opacity-100 pointer-events-auto"
                  : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
              }
            `}
            id="permission-toggle-button"
          >
            {isToggling ? (
              <>
                <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                {pendingAction === "remove"
                  ? t("PERMISSION_ASSIGNMENT.REMOVING")
                  : t("PERMISSION_ASSIGNMENT.ASSIGNING")}
              </>
            ) : permission.is_assigned ? (
              <>
                <Minus className="w-3 h-3" />
                {t("PERMISSION_ASSIGNMENT.REMOVE")}
              </>
            ) : (
              <>
                <Plus className="w-3 h-3" />
                {t("PERMISSION_ASSIGNMENT.ASSIGN")}
              </>
            )}
          </Button>
        </PermissionGuard>
      </div>
    </div>
  );
};

const PermissionRow = React.memo(PermissionRowComponent);
PermissionRow.displayName = "EmployeePermissionRow";

export const EmployeePermissionAssignmentView: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { employeeId } = useParams({ strict: false });

  const navigate = useNavigate();

  const { t } = useTranslation([
    "roleManagementPage",
    "employeeManagementPage",
    "common",
  ]);
  const { hasAnyPermission } = usePermissions();

  const canViewEmployeePermissions = hasAnyPermission([
    "can-view-permissions",
    "can-view-employee-assigned-permissions",
    "can-view-employee-available-permissions",
  ]);

  const canAssignEmployeePermissions = hasAnyPermission([
    "can-assign-permissions",
    "can-assign-permissions-to-employee",
  ]);

  const [filters, setFilters] = useState<TPermissionFiltersState>(
    DEFAULT_PERMISSION_FILTERS,
  );

  const [assignedPagination, setAssignedPagination] = useState<PaginationState>(
    {
      pageIndex: PAGE_INDEX,
      pageSize: PAGE_LIMIT_TEN,
    },
  );

  const [availablePagination, setAvailablePagination] =
    useState<PaginationState>({
      pageIndex: PAGE_INDEX,
      pageSize: PAGE_LIMIT_TEN,
    });

  const [selectedPermissionIds, setSelectedPermissionIds] =
    useState<TSelectedPermissionIdsState>({
      assign: [],
      remove: [],
    });
  const [pendingPermissionIds, setPendingPermissionIds] = useState<Set<string>>(
    new Set(),
  );
  const [pendingPermissionActions, setPendingPermissionActions] = useState<
    Map<string, "assign" | "remove">
  >(new Map());

  const [viewState, setViewState] = useState<TPermissionAssignmentViewState>({
    activeBulkAction: null,
    isNavigatingBack: false,
  });

  const breadcrumbRoutes = useMemo(() => {
    const routes = [
      {
        name: t("common:SIDEBAR_LABEL.EMPLOYEE_MANAGEMENT"),
        path: "/employee-management",
        icon: <Users />,
      },
    ];

    if (employeeId) {
      routes.push({
        name: t("PERMISSION_ASSIGNMENT.TITLE"),
        path: `/employee-management/assign-permission/${employeeId}`,
        icon: <Shield />,
      });
    }

    return routes;
  }, [employeeId, t]);
  useBreadcrumb(breadcrumbRoutes);

  const debouncedSearchQuery = useDebounce(filters.searchQuery, 2000);
  const { data: permissionModules, isLoading: isPermissionModulesLoading } =
    usePermissionModules({
      enabled: canViewEmployeePermissions,
    });

  // Memoized modules and available models
  const modules = useMemo(() => {
    return permissionModules?.modules ?? [];
  }, [permissionModules]);

  const availableModels = useMemo(() => {
    if (filters.selectedModuleKey === "all") return [];

    const module = modules.find(
      (m) => m.app_label === filters.selectedModuleKey,
    );
    return module?.models ?? [];
  }, [modules, filters.selectedModuleKey]);

  // Fetch assigned permissions
  const {
    data: assignedPermissionsData,
    isLoading: isAssignedInitialLoading,
    error: assignedPermissionsError,
    refetch: refetchAssignedPermissions,
    isFetching: isFetchingAssignedPermissions,
  } = useEmployeeAssignedPermissions(
    employeeId!,
    {
      search: debouncedSearchQuery || undefined,
      page: assignedPagination.pageIndex + 1,
      page_size: assignedPagination.pageSize,
      app_label:
        filters.selectedModuleKey !== "all"
          ? filters.selectedModuleKey
          : undefined,
      model:
        filters.selectedModelKey !== "all"
          ? filters.selectedModelKey
          : undefined,
    },
    {
      enabled: canViewEmployeePermissions,
      keepPreviousData: true,
    },
  );

  // Fetch available permissions
  const {
    data: availablePermissionsData,
    isLoading: isAvailableInitialLoading,
    error: availablePermissionsError,
    refetch: refetchAvailablePermissions,
    isFetching: isFetchingAvailablePermissions,
  } = useEmployeeAvailablePermissions(
    employeeId!,
    {
      search: debouncedSearchQuery || undefined,
      page: availablePagination.pageIndex + 1,
      page_size: availablePagination.pageSize,
      app_label:
        filters.selectedModuleKey !== "all"
          ? filters.selectedModuleKey
          : undefined,
      model:
        filters.selectedModelKey !== "all"
          ? filters.selectedModelKey
          : undefined,
    },
    {
      enabled: canViewEmployeePermissions,
    },
  );

  const refetchPermissionBoxes = useCallback(async () => {
    await Promise.allSettled([
      refetchAvailablePermissions(),
      refetchAssignedPermissions(),
    ]);
  }, [refetchAvailablePermissions, refetchAssignedPermissions]);

  const permissionToggleMutation = useEmployeePermissionToggle({
    onSuccess: async () => {
      await refetchPermissionBoxes();
    },
    onError: (
      error: TApiServiceError,
      variables: TEmployeePermissionToggleVariables,
    ) => {
      const actionText = variables.isAssigned ? "remove" : "assign";
      const apiMessage = error.message;
      const description: string =
        typeof apiMessage === "string" && apiMessage.trim().length > 0
          ? apiMessage
          : `An error occurred while trying to ${actionText} the permission.`;
      toast({
        title: `Failed to ${actionText} permission.`,
        description,
        variant: "destructive",
      });
    },
    onSettled: (
      _data,
      _error,
      variables: TEmployeePermissionToggleVariables,
    ) => {
      const permissionId = variables.permissionId;
      setPendingPermissionIds((prev) => {
        const updated = new Set(prev);
        updated.delete(permissionId);
        return updated;
      });
      setPendingPermissionActions((prev) => {
        const next = new Map(prev);
        next.delete(permissionId);
        return next;
      });
    },
  });

  const bulkAssignmentMutation = useBulkEmployeePermissionAssignment({
    onSuccess: async (response, variables) => {
      await refetchPermissionBoxes();
      const isAssign = variables.action === "assign";
      setSelectedPermissionIds((prev) =>
        isAssign ? { ...prev, assign: [] } : { ...prev, remove: [] },
      );
      const successCount = response.successful_operations ?? 0;
      const failedCount = response.failed_operations ?? 0;
      if (failedCount === 0) {
        toast({
          title: isAssign
            ? "Permissions assigned successfully."
            : "Permissions removed successfully.",
          description: isAssign
            ? `${successCount} permissions were assigned.`
            : `${successCount} permissions were removed.`,
          variant: "green",
        });
      } else {
        toast({
          title: isAssign
            ? "Permissions assigned with some issues."
            : "Permissions removed with some issues.",
          description: isAssign
            ? `${successCount} permissions assigned, ${failedCount} failed.`
            : `${successCount} permissions removed, ${failedCount} failed.`,
          variant: "yellow",
        });
      }
    },
    onError: (
      error: TApiServiceError,
      variables: TBulkEmployeePermissionAssignmentRequest,
    ) => {
      const isAssign = variables.action === "assign";
      const apiMessage = error.message;
      const description: string =
        typeof apiMessage === "string" && apiMessage.trim().length > 0
          ? apiMessage
          : isAssign
            ? "An error occurred while trying to assign permissions."
            : "An error occurred while trying to remove permissions.";
      toast({
        title: isAssign
          ? "Failed to assign permissions."
          : "Failed to remove permissions.",
        description,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setViewState((prev) => ({ ...prev, activeBulkAction: null }));
    },
  });

  const handleBackToEmployees = useCallback(async () => {
    if (viewState.isNavigatingBack) return;
    setViewState((prev) => ({ ...prev, isNavigatingBack: true }));
    try {
      await queryClient.invalidateQueries({
        queryKey: ["employee-list"],
        refetchType: "inactive",
      });
      await navigate({ to: "/employee-management" });
    } catch (error) {
      setViewState((prev) => ({ ...prev, isNavigatingBack: false }));
      throw error;
    }
  }, [viewState.isNavigatingBack, navigate, queryClient]);

  // Initial loading state
  const isInitialLoading =
    isAssignedInitialLoading || isAvailableInitialLoading;

  // KPI counts
  const assignedPermissionsKPICount = assignedPermissionsData?.count ?? 0;
  const availablePermissionsKPICount = availablePermissionsData?.count ?? 0;

  // Page counts
  const assignedPageCount = Math.ceil(
    assignedPermissionsKPICount / assignedPagination.pageSize,
  );

  const availablePageCount = Math.ceil(
    availablePermissionsKPICount / availablePagination.pageSize,
  );

  // Memoized assigned and available permission lists
  const assignedPermissions = useMemo(
    () => assignedPermissionsData?.results ?? [],
    [assignedPermissionsData],
  );

  const availablePermissions = useMemo(
    () => availablePermissionsData?.results ?? [],
    [availablePermissionsData],
  );

  // Handler for toggling a single permission
  const handlePermissionToggle = useCallback(
    (permission: TPermission) => {
      if (!canAssignEmployeePermissions) {
        return;
      }
      const permissionId = permission.internal_id;
      setPendingPermissionIds((prev) => new Set(prev).add(permissionId));
      setPendingPermissionActions((prev) => {
        const next = new Map(prev);
        next.set(permissionId, permission.is_assigned ? "remove" : "assign");
        return next;
      });

      permissionToggleMutation.mutate({
        employeeId: employeeId!,
        permissionId: permission.internal_id,
        isAssigned: permission.is_assigned,
      });
    },
    [canAssignEmployeePermissions, permissionToggleMutation, employeeId],
  );

  // Handler for bulk assign and unassign permissions
  const handleBulkAssign = useCallback(() => {
    if (!canAssignEmployeePermissions) return;
    if (selectedPermissionIds.assign.length === 0) return;
    setViewState((prev) => ({ ...prev, activeBulkAction: "assign" }));
    bulkAssignmentMutation.mutate({
      employee_id: employeeId!,
      permission_ids: selectedPermissionIds.assign,
      action: "assign",
    });
  }, [
    selectedPermissionIds.assign,
    bulkAssignmentMutation,
    employeeId,
    canAssignEmployeePermissions,
  ]);

  const handleBulkUnassign = useCallback(() => {
    if (!canAssignEmployeePermissions) return;
    if (selectedPermissionIds.remove.length === 0) return;
    setViewState((prev) => ({ ...prev, activeBulkAction: "remove" }));
    bulkAssignmentMutation.mutate({
      employee_id: employeeId!,
      permission_ids: selectedPermissionIds.remove,
      action: "remove",
    });
  }, [
    selectedPermissionIds.remove,
    bulkAssignmentMutation,
    employeeId,
    canAssignEmployeePermissions,
  ]);

  // Handler for selecting a permission
  const handleSelectPermission = useCallback(
    (permission: TPermission) => {
      if (!canAssignEmployeePermissions) return;
      const { internal_id, is_assigned } = permission;

      if (is_assigned) {
        // Permission is currently assigned -> move to unassign list
        setSelectedPermissionIds((prev) => ({
          ...prev,
          remove: prev.remove.includes(internal_id)
            ? prev.remove.filter((permissionId) => permissionId !== internal_id)
            : [...prev.remove, internal_id],
        }));
      } else {
        // Permission is currently unassigned -> move to assign list
        setSelectedPermissionIds((prev) => ({
          ...prev,
          assign: prev.assign.includes(internal_id)
            ? prev.assign.filter((permissionId) => permissionId !== internal_id)
            : [...prev.assign, internal_id],
        }));
      }
    },
    [canAssignEmployeePermissions],
  );

  // Check if a permission is selected
  const isChecked = (permission: TPermission) => {
    const { internal_id, is_assigned } = permission;

    if (is_assigned) {
      // Assigned items checked if in unassign list
      return selectedPermissionIds.remove.includes(internal_id);
    } else {
      // Unassigned items checked if in assign list
      return selectedPermissionIds.assign.includes(internal_id);
    }
  };

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_PERMISSION_FILTERS);
    setAssignedPagination({ pageIndex: PAGE_INDEX, pageSize: PAGE_LIMIT_TEN });
    setAvailablePagination({
      pageIndex: PAGE_INDEX,
      pageSize: PAGE_LIMIT_TEN,
    });
  }, []);

  // Reset pagination when RAW search changes (NOT debounced) and also  when filters change
  useEffect(() => {
    setAssignedPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setAvailablePagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [
    filters.searchQuery,
    filters.selectedModuleKey,
    filters.selectedModelKey,
  ]);

  const hasActiveFilters =
    filters.searchQuery !== "" ||
    filters.selectedModuleKey !== "all" ||
    filters.selectedModelKey !== "all";

  const selectedModuleLabel = useMemo(() => {
    if (filters.selectedModuleKey === "all") {
      return t("PERMISSION_ASSIGNMENT.ALL_MODULES");
    }

    const module = modules.find(
      (m) => m.app_label === filters.selectedModuleKey,
    );
    return module?.display_name ?? filters.selectedModuleKey;
  }, [filters.selectedModuleKey, modules, t]);

  const selectedModelLabel = useMemo(() => {
    if (filters.selectedModelKey === "all") {
      return t("PERMISSION_ASSIGNMENT.ALL_MODELS");
    }

    return (
      availableModels.find((m) => m.model === filters.selectedModelKey)
        ?.display_name ?? filters.selectedModelKey
    );
  }, [filters.selectedModelKey, availableModels, t]);

  const isAnySingleActionPending = pendingPermissionIds.size > 0;
  const getPendingAction = useCallback(
    (permissionId: string): "assign" | "remove" | undefined =>
      pendingPermissionActions.get(permissionId),
    [pendingPermissionActions],
  );

  const isBulkAssigning =
    bulkAssignmentMutation.isPending && viewState.activeBulkAction === "assign";

  const isBulkRemoving =
    bulkAssignmentMutation.isPending && viewState.activeBulkAction === "remove";

  const renderPaginationFooter = (
    pagination: PaginationState,
    setPagination: React.Dispatch<React.SetStateAction<PaginationState>>,
    totalRecordCount: number,
    pageCount: number,
  ) => (
    <div className="p-4 border-t border-border flex-shrink-0">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 dropdown-main-section justify-end">
          <Typography variant="small" className="text-muted-foreground">
            {t("GLOBAL.SHOW")}:
          </Typography>
          <Select
            name="permission-page-size"
            options={PAGE_SIZE_OPTIONS.map((size) => ({
              value: size.toString(),
              label: size.toString(),
            }))}
            value={{
              value: pagination.pageSize.toString(),
              label: pagination.pageSize.toString(),
            }}
            onChange={(option: TSelectOption) => {
              setPagination({
                pageIndex: PAGE_INDEX,
                pageSize: Number(option.value),
              });
            }}
            getOptionLabel={(option: TSelectOption) => option.label}
            getOptionValue={(option: TSelectOption) => option.value}
            placeholder={t("GLOBAL.SHOW")}
            className="w-20 text-sm"
            isClearable={false}
            isSearchable={false}
          />
        </div>
        <div className="mt-auto ">
          <CommonPaginationCard
            totalRecordCount={totalRecordCount}
            page={pagination.pageIndex}
            pageCount={pageCount}
            rowsPerPage={pagination.pageSize}
            onPageChange={(page) =>
              setPagination((prev) => ({
                ...prev,
                pageIndex: page,
              }))
            }
            onRowsPerPageChange={(size) =>
              setPagination({
                pageIndex: 0,
                pageSize: size,
              })
            }
            disablePrev={pagination.pageIndex === 0}
            disableNext={pagination.pageIndex >= pageCount - 1}
          />
        </div>
      </div>
    </div>
  );

  const permissionApiError =
    assignedPermissionsError ?? availablePermissionsError ?? undefined;
  const hasPermissionApiError = permissionApiError != null;

  const renderPermissionKpiCard = (
    type: "assigned" | "available",
    count: number,
    title: string,
  ) => {
    const kpiConfig = {
      assigned: {
        icon: <CheckCircle className="w-6 h-6 text-green" />,
        bg: "bg-green/10",
      },
      available: {
        icon: <Circle className="w-6 h-6 text-muted-foreground" />,
        bg: "bg-gray/20 dark:bg-gray-700",
      },
    } as const;
    const { icon, bg } = kpiConfig[type];

    return (
      <CommonCardUI
        className="custom-bx-shadow bg-background transition hover:-translate-y-1 hover:shadow-xl"
        classNameContent="p-4"
      >
        <div className="flex items-center gap-4">
          <div
            className={cn(
              "w-12 h-12 rounded-lg flex items-center justify-center",
              bg,
            )}
          >
            {icon}
          </div>
          <div className="flex flex-col">
            <Typography
              variant="h3"
              affects="semiBold"
              className="text-foreground"
            >
              {count}
            </Typography>
            <Typography
              variant="p"
              affects="semiBold"
              className="text-muted-foreground"
            >
              {title}
            </Typography>
          </div>
        </div>
      </CommonCardUI>
    );
  };

  return (
    <div>
      <Button
        variant="link"
        size="sm"
        type="button"
        onClick={() => void handleBackToEmployees()}
        className="text-gray p-0 h-auto"
        disabled={viewState.isNavigatingBack}
        id="employee-permission-assign-back-link"
      >
        {viewState.isNavigatingBack ? (
          <span className="inline-flex items-center gap-2">
            <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
            {t("employeeManagementPage:BACK_TO_LIST")}
          </span>
        ) : (
          t("employeeManagementPage:BACK_TO_LIST")
        )}
      </Button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        {renderPermissionKpiCard(
          "assigned",
          assignedPermissionsData?.count ?? 0,
          t(
            "employeeManagementPage:EMPLOYEE_PERMISSION_ASSIGNMENT.ASSIGNED_PERMISSIONS",
          ),
        )}
        {renderPermissionKpiCard(
          "available",
          availablePermissionsData?.count ?? 0,
          t(
            "employeeManagementPage:EMPLOYEE_PERMISSION_ASSIGNMENT.AVAILABLE_PERMISSIONS",
          ),
        )}
      </div>

      <ErrorFallback isError={hasPermissionApiError} error={permissionApiError}>
        <CommonCardUI
          className="custom-bx-shadow bg-background"
          classNameContent="p-0"
        >
          <section className="border-b border-dashed p-3 space-y-2">
            <Typography affects="semiBold" className="mt-0">
              {t("PERMISSION_ASSIGNMENT.TITLE")}
            </Typography>
            <Typography variant="small" className="text-muted-foreground">
              {t("PERMISSION_ASSIGNMENT.MANAGE_PERMISSIONS")}
            </Typography>
          </section>
          <div className="border-b border-dashed p-3">
            {/* Search and Filters */}
            {isPermissionModulesLoading ? (
              <div className="border dark:border-gray-700 rounded-lg shadow p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <KpiLoaders numberOfLoaders={1} className="flex-1 h-10" />
                  <KpiLoaders numberOfLoaders={1} className="w-40 h-10" />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Search, Module, Model filters and Clear button in one row */}
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search */}
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />

                      <Input
                        type="text"
                        placeholder={t(
                          "PERMISSION_ASSIGNMENT.SEARCH_PLACEHOLDER",
                        )}
                        value={filters.searchQuery}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            searchQuery: e.target.value,
                          }))
                        }
                        className={`pl-10 py-2 w-full ${isInitialLoading ? "pr-10" : "pr-4"}`}
                      />
                    </div>
                  </div>

                  {/* Modules Dropdown*/}
                  <div className="flex-1">
                    <div className="dropdown-main-section items-center">
                      <Dropdown
                        key={filters.selectedModuleKey}
                        label={selectedModuleLabel}
                        dismissOnClick={true}
                        size="default"
                        className="w-full flex items-center justify-between rounded border border-gray-300 bg-background px-2 py-2 text-sm text-foreground hover:border-gray-400 dark:border-gray-700"
                      >
                        <DropdownItem
                          key="all"
                          className="hover:bg-slate-200"
                          onClick={() => {
                            setFilters((prev) => ({
                              ...prev,
                              selectedModuleKey: "all",
                              selectedModelKey: "all",
                            }));
                          }}
                        >
                          {t("PERMISSION_ASSIGNMENT.ALL_MODULES")}
                        </DropdownItem>

                        {modules?.map((module) => (
                          <DropdownItem
                            key={module.app_label}
                            className="hover:bg-slate-200"
                            onClick={() => {
                              setFilters((prev) => ({
                                ...prev,
                                selectedModuleKey: module.app_label,
                                selectedModelKey: "all",
                              }));
                            }}
                          >
                            {module.display_name}
                          </DropdownItem>
                        ))}
                      </Dropdown>
                    </div>
                  </div>

                  {/* Models Dropdown*/}
                  <div className="flex-1">
                    <div className="dropdown-main-section items-center">
                      <Dropdown
                        key={filters.selectedModelKey}
                        label={selectedModelLabel}
                        dismissOnClick={true}
                        size="default"
                        disabled={filters.selectedModuleKey === "all"}
                        className={`w-full flex items-center justify-between rounded border bg-background px-2 py-2 text-sm text-foreground 
                      ${filters.selectedModuleKey === "all" ? "cursor-not-allowed" : ""}`}
                      >
                        <DropdownItem
                          key="all"
                          className="hover:bg-slate-200"
                          onClick={() =>
                            setFilters((prev) => ({
                              ...prev,
                              selectedModelKey: "all",
                            }))
                          }
                        >
                          {t("PERMISSION_ASSIGNMENT.ALL_MODELS")}
                        </DropdownItem>

                        {availableModels?.map((model) => (
                          <DropdownItem
                            key={model?.model}
                            className="hover:bg-slate-200"
                            onClick={() =>
                              setFilters((prev) => ({
                                ...prev,
                                selectedModelKey: model?.model,
                              }))
                            }
                          >
                            {model?.display_name}
                          </DropdownItem>
                        ))}
                      </Dropdown>
                    </div>
                  </div>

                  {/* Clear Filters Button */}
                  <div className="flex-shrink-0">
                    {hasActiveFilters && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearFilters}
                      >
                        {t("GLOBAL.CLEAR_FILTERS")}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-3">
            {/* Available Permissions */}
            <div className="border border-gray-300 dark:border-gray-700 rounded-lg h-[400px] flex flex-col">
              <div className="px-3 py-2 border-b border-border flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Typography variant="p" className="font-semibold text-base">
                      {t("PERMISSION_ASSIGNMENT.AVAILABLE_PERMISSIONS")}
                    </Typography>
                    <Badge
                      variant="secondary"
                      className="text-xs px-2 py-0.5 min-h-0 leading-none"
                    >
                      {availablePermissionsKPICount}
                    </Badge>
                  </div>
                  {selectedPermissionIds.assign.length > 0 && (
                    <PermissionGuard
                      permissions={[
                        "can-assign-permissions",
                        "can-assign-permissions-to-employee",
                      ]}
                    >
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => void handleBulkAssign()}
                        disabled={
                          selectedPermissionIds.assign.length === 0 ||
                          isAnySingleActionPending ||
                          bulkAssignmentMutation.isPending
                        }
                      >
                        {isBulkAssigning ? (
                          <>
                            <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin mr-2" />
                            {t("PERMISSION_ASSIGNMENT.ASSIGNING")}
                          </>
                        ) : (
                          `${t("PERMISSION_ASSIGNMENT.ASSIGN_SELECTED")} (${selectedPermissionIds.assign.length})`
                        )}
                      </Button>
                    </PermissionGuard>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto min-h-0 p-3">
                {isFetchingAvailablePermissions ? (
                  <KpiLoaders numberOfLoaders={5} />
                ) : availablePermissions?.length > 0 ? (
                  availablePermissions?.map((permission) => (
                    <PermissionRow
                      key={permission.internal_id}
                      permission={permission}
                      isChecked={isChecked(permission)}
                      isToggling={pendingPermissionIds.has(
                        permission.internal_id,
                      )}
                      pendingAction={getPendingAction(permission.internal_id)}
                      isBulkActionRunning={bulkAssignmentMutation.isPending}
                      canAssignEmployeePermissions={
                        canAssignEmployeePermissions
                      }
                      onSelect={handleSelectPermission}
                      onToggle={handlePermissionToggle}
                    />
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Circle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <Typography
                      variant="p"
                      affects="muted"
                      className="dark:text-muted-foreground"
                    >
                      {t("PERMISSION_ASSIGNMENT.NO_AVAILABLE_PERMISSIONS")}
                    </Typography>
                  </div>
                )}
              </div>

              {availablePermissions?.length > 0 &&
                renderPaginationFooter(
                  availablePagination,
                  setAvailablePagination,
                  availablePermissionsData?.count ?? 0,
                  availablePageCount,
                )}
            </div>

            {/* Assigned Permissions */}
            <div className="border border-gray-300 dark:border-gray-700 rounded-lg h-[400px] flex flex-col">
              <div className="px-3 py-2 border-b border-border flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Typography variant="p" className="font-semibold text-base">
                      {t("PERMISSION_ASSIGNMENT.ASSIGNED_PERMISSIONS")}
                    </Typography>
                    <Badge
                      variant="default"
                      className="text-xs px-2 py-0.5 min-h-0 leading-none"
                    >
                      {assignedPermissionsKPICount}
                    </Badge>
                  </div>
                  {selectedPermissionIds.remove.length > 0 && (
                    <PermissionGuard
                      permissions={[
                        "can-assign-permissions",
                        "can-assign-permissions-to-employee",
                      ]}
                    >
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => void handleBulkUnassign()}
                        disabled={
                          selectedPermissionIds.remove.length === 0 ||
                          isAnySingleActionPending ||
                          bulkAssignmentMutation.isPending
                        }
                      >
                        {isBulkRemoving ? (
                          <>
                            <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin mr-2" />
                            {t("PERMISSION_ASSIGNMENT.REMOVING")}
                          </>
                        ) : (
                          `${t("PERMISSION_ASSIGNMENT.REMOVE_SELECTED")} (${selectedPermissionIds.remove.length})`
                        )}
                      </Button>
                    </PermissionGuard>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto min-h-0 p-3">
                {isFetchingAssignedPermissions ? (
                  <KpiLoaders numberOfLoaders={5} />
                ) : assignedPermissions.length > 0 ? (
                  assignedPermissions.map((permission) => (
                    <PermissionRow
                      key={permission.id}
                      permission={permission}
                      isChecked={isChecked(permission)}
                      isToggling={pendingPermissionIds.has(
                        permission.internal_id,
                      )}
                      pendingAction={getPendingAction(permission.internal_id)}
                      isBulkActionRunning={bulkAssignmentMutation.isPending}
                      canAssignEmployeePermissions={
                        canAssignEmployeePermissions
                      }
                      onSelect={handleSelectPermission}
                      onToggle={handlePermissionToggle}
                    />
                  ))
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <Typography
                      variant="p"
                      affects="muted"
                      className="dark:text-muted-foreground"
                    >
                      {t("PERMISSION_ASSIGNMENT.NO_ASSIGNED_PERMISSIONS")}
                    </Typography>
                  </div>
                )}
              </div>

              {assignedPermissions?.length > 0 &&
                renderPaginationFooter(
                  assignedPagination,
                  setAssignedPagination,
                  assignedPermissionsData?.count ?? 0,
                  assignedPageCount,
                )}
            </div>
          </div>
        </CommonCardUI>
      </ErrorFallback>
    </div>
  );
};
